import type { Box } from '@/services/tour/tourModel'

const RADIUS = 8

/** A rounded rectangle, drawn the other way round so `evenodd` cuts it out of the cover. */
function roundedRect({ top: y, left: x, width: w, height: h }: Box): string {
  const r = Math.min(RADIUS, w / 2, h / 2)
  return [
    `M${x + r},${y}`,
    `H${x + w - r}`,
    `A${r},${r} 0 0 1 ${x + w},${y + r}`,
    `V${y + h - r}`,
    `A${r},${r} 0 0 1 ${x + w - r},${y + h}`,
    `H${x + r}`,
    `A${r},${r} 0 0 1 ${x},${y + h - r}`,
    `V${y + r}`,
    `A${r},${r} 0 0 1 ${x + r},${y}`,
    'Z',
  ].join(' ')
}

interface TourScrimProps {
  viewport: { width: number; height: number }
  /** The hole, or none: the whole page dims. */
  hole: Box | null
}

/**
 * The page dimmed to about 40% of `background`, with a hole around the step's target
 * (spec §1). Only the painted cover takes the pointer: a click in the hole reaches the page.
 */
export function TourScrim({ viewport, hole }: TourScrimProps) {
  const cover = `M0,0 H${viewport.width} V${viewport.height} H0 Z`
  return (
    <svg
      aria-hidden="true"
      data-testid="tour-scrim"
      className="pointer-events-none fixed inset-0 h-full w-full"
      width={viewport.width}
      height={viewport.height}
    >
      <path
        d={hole ? `${cover} ${roundedRect(hole)}` : cover}
        fillRule="evenodd"
        className="fill-background/60"
        style={{ pointerEvents: 'visiblePainted' }}
        data-hole={hole ? 'yes' : 'no'}
      />
    </svg>
  )
}
